import React from "react";
import { normalizeError, getErrorMessage, isRetryableError } from "./errorHandler.js";
import { CALENDAR_CONFIG } from "./calendarConstants.js";

/**
 * ErrorDisplay Component
 * Provides consistent error display UI with retry functionality
 */
const ErrorDisplay = ({ 
    error, 
    title = "Error Loading Calendar Data",
    onRetry = null,
    showRetry = true,
    className = "",
    context = "",
}) => {
    if (!error) return null;

    const normalizedError = normalizeError(error);
    const errorInfo = getErrorMessage(normalizedError, context);
    const canRetry = showRetry && onRetry && isRetryableError(normalizedError);

    return (
        <div 
            className={`error-display ${className}`}
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100vh',
                padding: '20px',
                backgroundColor: CALENDAR_CONFIG.UI_COLORS.BG_LOADING,
                color: CALENDAR_CONFIG.UI_COLORS.TEXT_PRIMARY,
            }}
        >
            <div
                style={{
                    maxWidth: '500px',
                    textAlign: 'center',
                    padding: '30px',
                    backgroundColor: CALENDAR_CONFIG.UI_COLORS.BG_WHITE,
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                }}
            >
                <div
                    style={{
                        fontSize: '48px',
                        marginBottom: '16px',
                    }}
                >
                    ⚠️
                </div>
                <h2
                    style={{
                        fontSize: '20px',
                        fontWeight: '600',
                        marginBottom: '12px',
                        color: CALENDAR_CONFIG.UI_COLORS.TEXT_PRIMARY,
                    }}
                >
                    {title}
                </h2>
                <p
                    style={{
                        fontSize: '16px',
                        color: CALENDAR_CONFIG.UI_COLORS.TEXT_PRIMARY,
                        marginBottom: '12px',
                        lineHeight: '1.5',
                        fontWeight: '500',
                    }}
                >
                    {errorInfo.message}
                </p>
                {errorInfo.action && (
                    <p
                        style={{
                            fontSize: '14px',
                            color: CALENDAR_CONFIG.UI_COLORS.TEXT_SECONDARY,
                            marginBottom: '24px',
                            lineHeight: '1.5',
                        }}
                    >
                        {errorInfo.action}
                    </p>
                )}
                {canRetry && (
                    <button
                        onClick={onRetry}
                        aria-label="Retry loading calendar data"
                        style={{
                            padding: '10px 24px',
                            backgroundColor: '#2563eb',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s',
                        }}
                        onMouseOver={(e) => e.target.style.backgroundColor = '#1d4ed8'}
                        onMouseOut={(e) => e.target.style.backgroundColor = '#2563eb'}
                        onFocus={(e) => e.target.style.outline = '2px solid #2563eb'}
                        onBlur={(e) => e.target.style.outline = 'none'}
                    >
                        Try Again
                    </button>
                )}
            </div>
        </div>
    );
};

/**
 * MultipleErrorsDisplay Component
 * Displays multiple errors in a consolidated view
 */
export const MultipleErrorsDisplay = ({ 
    errors = [],
    onRetry = null,
    className = "",
}) => {
    if (!errors || errors.length === 0) return null;

    const normalizedErrors = errors
        .filter(Boolean)
        .map((error) => normalizeError(error));

    if (normalizedErrors.length === 0) return null;

    // If only one error, use the single error display
    if (normalizedErrors.length === 1) {
        return (
            <ErrorDisplay
                error={normalizedErrors[0]}
                onRetry={onRetry}
                className={className}
            />
        );
    }

    // Multiple errors - show consolidated view
    const hasRetryableError = normalizedErrors.some((err) => isRetryableError(err));
    const canRetry = onRetry && hasRetryableError;

    return (
        <div
            className={`error-display multiple-errors ${className}`}
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100vh',
                padding: '20px',
                backgroundColor: CALENDAR_CONFIG.UI_COLORS.BG_LOADING,
                color: CALENDAR_CONFIG.UI_COLORS.TEXT_PRIMARY,
            }}
        >
            <div
                style={{
                    maxWidth: '600px',
                    textAlign: 'center',
                    padding: '30px',
                    backgroundColor: CALENDAR_CONFIG.UI_COLORS.BG_WHITE,
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                }}
            >
                <div
                    style={{
                        fontSize: '48px',
                        marginBottom: '16px',
                    }}
                >
                    ⚠️
                </div>
                <h2
                    style={{
                        fontSize: '20px',
                        fontWeight: '600',
                        marginBottom: '12px',
                        color: CALENDAR_CONFIG.UI_COLORS.TEXT_PRIMARY,
                    }}
                >
                    Multiple Errors Occurred
                </h2>
                <div
                    style={{
                        marginBottom: '24px',
                    }}
                >
                    {normalizedErrors.map((error, index) => {
                        const errorInfo = getErrorMessage(error);
                        return (
                            <div
                                key={index}
                                style={{
                                    marginBottom: '12px',
                                    padding: '12px',
                                    backgroundColor: '#fef2f2',
                                    borderRadius: '4px',
                                    textAlign: 'left',
                                }}
                            >
                                <p
                                    style={{
                                        fontSize: '14px',
                                        color: CALENDAR_CONFIG.UI_COLORS.TEXT_PRIMARY,
                                        marginBottom: '4px',
                                        fontWeight: '500',
                                    }}
                                >
                                    {errorInfo.message}
                                </p>
                                {errorInfo.action && (
                                    <p
                                        style={{
                                            fontSize: '13px',
                                            color: CALENDAR_CONFIG.UI_COLORS.TEXT_SECONDARY,
                                            margin: 0,
                                        }}
                                    >
                                        {errorInfo.action}
                                    </p>
                                )}
                            </div>
                        );
                    })}
                </div>
                {canRetry && (
                    <button
                        onClick={onRetry}
                        style={{
                            padding: '10px 24px',
                            backgroundColor: '#2563eb',
                            color: CALENDAR_CONFIG.UI_COLORS.TEXT_ACTIVE,
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s',
                        }}
                        onMouseOver={(e) => e.target.style.backgroundColor = '#1d4ed8'}
                        onMouseOut={(e) => e.target.style.backgroundColor = '#2563eb'}
                    >
                        Try Again
                    </button>
                )}
            </div>
        </div>
    );
};

export default ErrorDisplay;

